import {
  Box,
  Card,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import moment from "moment-timezone";
import React, { useMemo } from "react";
import { ENUM_USER_ROLE } from "../../enums";
import { APP_COLORS } from "../../enums/colors";
import FlexBox from "../../components/layouts/Flexbox";
import { BrowserTimezoneAlert } from "../../components/BrowserTimezoneAlert";

type SlotTime = {
  from: string;
  to: string;
};

type SlotHistorySet = {
  slots: SlotTime[];
  updatedAt: string;
};

export type SlotHistoryData = {
  availableTimeSlots?: SlotHistorySet[];
  suggestedTimeSlots?: SlotHistorySet[];
} | null;

type DateSlotGroup = {
  date: string;
  day: number;
  month: string;
  slots: Array<{
    id: string;
    timeSlot: string;
    timeFrom: string;
    timeTo: string;
  }>;
  updatedAt: string;
  updatedAtFormatted: string;
};

interface SlotHistorySectionProps {
  history?: SlotHistoryData;
  viewerRole?: ENUM_USER_ROLE | null;
  heading?: string;
  showHeading?: boolean;
  emptyState?: React.ReactNode;
  maxHeight?: number;
}

const formatSlotsByDate = (
  slotSets?: SlotHistorySet[]
): DateSlotGroup[] => {
  if (!slotSets || !slotSets.length) return [];

  const dateGroups: Record<string, DateSlotGroup> = {};

  slotSets.forEach((slotSet, setIndex) => {
    const updatedAt = slotSet.updatedAt || "";
    const updatedAtFormatted = updatedAt
      ? moment(updatedAt).format("DD MMMM, YYYY HH:mm")
      : "N/A";

    slotSet.slots.forEach((slot, slotIndex) => {
      if (slot.from && slot.to) {
        const fromMoment = moment(slot.from);
        const dateKey = fromMoment.format("YYYY-MM-DD");
        const timeFrom = fromMoment.format("HH:mm");
        const timeTo = moment(slot.to).format("HH:mm");
        const timeSlot = `${timeFrom}-${timeTo}`;

        if (!dateGroups[dateKey]) {
          dateGroups[dateKey] = {
            date: dateKey,
            day: fromMoment.date(),
            month: fromMoment.format("MMM"),
            slots: [],
            updatedAt: updatedAt,
            updatedAtFormatted: updatedAtFormatted,
          };
        }

        dateGroups[dateKey].slots.push({
          id: `set-${setIndex}-slot-${slotIndex}`,
          timeFrom,
          timeTo,
          timeSlot,
        });
      }
    });
  });

  return Object.values(dateGroups)
    .map((group) => ({
      ...group,
      slots: group.slots.sort((a, b) => a.timeFrom.localeCompare(b.timeFrom)),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

const getAvailableSlotsLabel = (
  viewerRole?: ENUM_USER_ROLE | null
): string => {
  if (viewerRole === ENUM_USER_ROLE.VENDOR || viewerRole === ENUM_USER_ROLE.DELEGATE_VENDOR) {
    return "Your Available Slots";
  }

  if (viewerRole === ENUM_USER_ROLE.AMBASSADOR) {
    return "Vendor Available Time";
  }

  return "Available Slots";
};

const getSuggestedSlotsLabel = (
  viewerRole?: ENUM_USER_ROLE | null
): string => {
  if (viewerRole === ENUM_USER_ROLE.AMBASSADOR) {
    return "Your Suggested Slots";
  }

  if (viewerRole === ENUM_USER_ROLE.VENDOR || viewerRole === ENUM_USER_ROLE.DELEGATE_VENDOR) {
    return "Ambassador Suggested Slots";
  }

  return "Suggested Slots";
};

interface DateSlotCardProps {
  group: DateSlotGroup;
  chipColor: string;
  chipTextColor: string;
}

function DateSlotCard({ group, chipColor, chipTextColor }: DateSlotCardProps) {
  return (
    <Card
      sx={{ borderRadius: 2, overflow: "hidden" }}
    >
      <FlexBox>
        <Stack
          bgcolor={APP_COLORS.greyLight2}
          px={1}
          alignItems="center"
          justifyContent="center"
          borderRadius={4}
          height={70}
          width={70}
        >
          <Typography
            variant="h4"
            fontWeight={700}
            color={APP_COLORS.text}
            sx={{ lineHeight: 1 }}
          >
            {group.day}
          </Typography>
          <Typography
            variant="body-ibmPlexMono"
            color={APP_COLORS.textSecondary}
            fontSize={12}
            mt={0.5}
          >
            {group.month}
          </Typography>
        </Stack>
        <Box flex={1} p={2}>
          <Stack gap={1.5}>
            <FlexBox flexWrap="wrap" gap={1}>
              {group.slots.map((slot) => (
                <Chip
                  key={slot.id}
                  icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  label={slot.timeSlot}
                  size="small"
                  sx={{
                    bgcolor: chipColor,
                    color: chipTextColor,
                    "& .MuiChip-icon": {
                      color: chipTextColor,
                    },
                    height: 32,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
              ))}
            </FlexBox>
            <FlexBox alignItems="center" gap={0.5}>
              <HistoryIcon sx={{ fontSize: 14, color: APP_COLORS.textSecondary }} />
              <Typography
                variant="body-ibmPlexMono"
                color={APP_COLORS.text}
                fontSize={11}
              >
                {group.updatedAtFormatted}
              </Typography>
            </FlexBox>
          </Stack>
        </Box>
      </FlexBox>
    </Card>
  );
}

interface SlotColumnProps {
  label: string;
  groups: DateSlotGroup[];
  chipColor: string;
  chipTextColor: string;
}

function SlotColumn({ label, groups, chipColor, chipTextColor }: SlotColumnProps) {
  if (groups.length === 0) return null;

  return (
    <Stack gap={1}>
      <Typography variant="h6" fontWeight={600} fontSize={16}>
        {label}
      </Typography>
      <Stack gap={1}>
        {groups.map((group) => (
          <DateSlotCard
            key={group.date}
            group={group}
            chipColor={chipColor}
            chipTextColor={chipTextColor}
          />
        ))}
      </Stack>
    </Stack>
  );
}

const SlotHistorySection: React.FC<SlotHistorySectionProps> = ({
  history,
  viewerRole,
  heading = "Times History",
  showHeading = true,
  emptyState,
  maxHeight,
}) => {
  const { vendorGroups, ambassadorGroups } = useMemo(() => {
    return {
      vendorGroups: formatSlotsByDate(history?.availableTimeSlots),
      ambassadorGroups: formatSlotsByDate(history?.suggestedTimeSlots),
    };
  }, [history?.availableTimeSlots, history?.suggestedTimeSlots]);

  if (!history) {
    return null;
  }

  if (!vendorGroups.length && !ambassadorGroups.length) {
    return emptyState ? <>{emptyState}</> : null;
  }

  const vendorLabel = getAvailableSlotsLabel(viewerRole);
  const ambassadorLabel = getSuggestedSlotsLabel(viewerRole);
  const renderDualColumn = vendorGroups.length > 0 && ambassadorGroups.length > 0;

  const vendorChipColor = APP_COLORS.primaryLight;
  const vendorChipTextColor = APP_COLORS.primary;
  const ambassadorChipColor = APP_COLORS.yellowLight;
  const ambassadorChipTextColor = APP_COLORS.yellowDark3;

  return (
    <Box>
      {showHeading && (
        <Typography variant="h6" fontWeight={600} mb={3}>
          {heading}
        </Typography>
      )}

      <Box my={1}>
        <BrowserTimezoneAlert />
      </Box>

      <Grid container spacing={2}>
        {vendorGroups.length > 0 && (
          <Grid item xs={12} md={renderDualColumn ? 6 : 12}>
            <Box
              maxHeight={maxHeight || "none"}
              sx={{ overflowY: maxHeight ? "auto" : "visible" }}
            >
              <SlotColumn
                label={vendorLabel}
                groups={vendorGroups}
                chipColor={vendorChipColor}
                chipTextColor={vendorChipTextColor}
              />
            </Box>
          </Grid>
        )}

        {ambassadorGroups.length > 0 && (
          <Grid item xs={12} md={renderDualColumn ? 6 : 12}>
            <Box
              maxHeight={maxHeight || "none"}
              sx={{ overflowY: maxHeight ? "auto" : "visible" }}
            >
              <SlotColumn
                label={ambassadorLabel}
                groups={ambassadorGroups}
                chipColor={ambassadorChipColor}
                chipTextColor={ambassadorChipTextColor}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default SlotHistorySection;
